import React, { useState, useEffect, useRef, useCallback } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  LinearProgress,
  Avatar,
  useTheme,
} from "@mui/material";

import { reconnectionTimerManager } from "../../services/reconnectionTimerManager";
import Row from "./core/Row";

// Extend dayjs with duration plugin
dayjs.extend(duration);

interface ReconnectionPopupProps {
  isVisible: boolean;
  onRetry: () => Promise<boolean>;
  onClose: () => void;
}

const ReconnectionPopup: React.FC<ReconnectionPopupProps> = ({
  isVisible,
  onRetry,
  onClose,
}) => {
  const theme = useTheme();
  const [countdown, setCountdown] = useState(1);
  const [isRetrying, setIsRetrying] = useState(false);
  const [shouldClose, setShouldClose] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const instanceIdRef = useRef(Math.random().toString(36).substr(2, 9));
  const prevIsVisibleRef = useRef(isVisible);

  const MAX_RETRIES = 5;

  // Format elapsed time in human-readable format using dayjs
  const formatElapsedTime = (seconds: number): string => {
    if (seconds < 60) {
      return "less than a minute";
    } else if (seconds < 120) {
      return "1 minute";
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} minutes`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (minutes === 0) {
        return hours === 1 ? "1 hour" : `${hours} hours`;
      } else {
        const hourText = hours === 1 ? "1 hour" : `${hours} hours`;
        const minuteText = minutes === 1 ? "1 minute" : `${minutes} minutes`;
        return `${hourText} ${minuteText}`;
      }
    }
  };

  const handleAutoRetry = useCallback(async () => {
    const currentRetryCount = reconnectionTimerManager.getRetryCount();

    if (currentRetryCount >= MAX_RETRIES) {
      return;
    }

    setIsRetrying(true);

    try {
      const success = await onRetry();

      if (success) {
        setShouldClose(true);
      }
      // Continue with countdown for next retry if failed
    } catch (error) {
      // Handle retry error silently
    } finally {
      setIsRetrying(false);
    }
  }, [onRetry]);

  const handleManualRetry = useCallback(async () => {
    // Reset the timer manager
    reconnectionTimerManager.stop();

    // Reset state
    setCountdown(1);
    setElapsedTime(0);
    setShouldClose(false); // Reset shouldClose state

    // Start fresh retry cycle
    reconnectionTimerManager.start(
      instanceIdRef.current,
      (countdownValue, elapsed) => {
        setCountdown(countdownValue);
        setElapsedTime(elapsed);
      },
      () => {
        handleAutoRetry();
      }
    );
  }, [handleAutoRetry]);

  // Handle popup visibility changes
  useEffect(() => {
    // Only run if isVisible actually changed
    if (isVisible === prevIsVisibleRef.current) {
      return;
    }

    prevIsVisibleRef.current = isVisible;

    if (!isVisible) {
      // Only stop timer if it's still active
      if (reconnectionTimerManager.isTimerActive()) {
        reconnectionTimerManager.stop();
      }
      setCountdown(1);
      setIsRetrying(false);
      setElapsedTime(0);
      setShouldClose(false); // Reset shouldClose state
      return;
    }

    // When popup becomes visible, start the retry cycle
    if (isVisible) {
      // Reset state
      setCountdown(1);
      setElapsedTime(0);
      setShouldClose(false); // Reset shouldClose state

      // Start the singleton timer
      reconnectionTimerManager.start(
        instanceIdRef.current,
        (countdownValue, elapsed) => {
          setCountdown(countdownValue);
          setElapsedTime(elapsed);
        },
        () => {
          handleAutoRetry();
        }
      );
    }
  }, [isVisible, handleAutoRetry]);

  // Handle popup close
  useEffect(() => {
    if (shouldClose) {
      // Don't stop timer here - let the isVisible useEffect handle it
      onClose();
    }
  }, [shouldClose, onClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Only stop timer if it's still active
      if (reconnectionTimerManager.isTimerActive()) {
        reconnectionTimerManager.stop();
      }
    };
  }, []);

  const currentRetryCount = reconnectionTimerManager.getRetryCount();
  const isMaxRetriesReached = currentRetryCount >= MAX_RETRIES;

  return (
    <Dialog
      open={isVisible}
      onClose={() => setShouldClose(true)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          position: 'relative',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pb: 2,
        }}
      >
        <Avatar
          sx={{
            bgcolor: '#f97316',
            width: 40,
            height: 40,
            fontSize: '1.25rem',
          }}
        >
          📡
        </Avatar>
        <Box>
          <Typography
            variant="h6"
            component="h3"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Connection Lost
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              mt: 0.5,
            }}
          >
            Network connectivity issue detected
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            mb: 2,
            mt: 2,
          }}
        >
          The connection to the server has been lost.
          <br />
          {isMaxRetriesReached
            ? "Auto-retry attempts exhausted. Please try manually."
            : "We're working to restore your connection..."}
        </Typography>

        <Row
        sx={{
          px: 3,
          pt: 2,
          gap: 1.5,
        }}
      >
        {isMaxRetriesReached ? (
          <Button
            onClick={handleManualRetry}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Retry Connection
          </Button>
        ) : (
          <>
            <Button
              onClick={handleManualRetry}
              disabled={isRetrying}
              variant="outlined"
              sx={{
                flex: 1,
              }}
            >
              Retry Now
            </Button>
            <Button
              onClick={() => setShouldClose(true)}
              variant="outlined"
              sx={{
                flex: 1,
                backgroundColor: theme.palette.action.hover,
                '&:hover': {
                  backgroundColor: theme.palette.action.selected,
                },
              }}
            >
              Dismiss
            </Button>
          </>
        )}
      </Row>

        {/* Status Message */}
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              textAlign: 'center',
              display: 'block',
            }}
          >
            You've been offline for {formatElapsedTime(elapsedTime)}.
            {isRetrying
              ? " Retrying..."
              : isMaxRetriesReached
              ? ""
              : ` Retrying in ${countdown} second${
                  countdown !== 1 ? "s" : ""
                }...`}
          </Typography>
        </Box>

        {/* Progress Bar */}
        {elapsedTime < 65 && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min((elapsedTime / 65) * 100, 100)}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: theme.palette.divider,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.dark || theme.palette.primary.main})`,
                },
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReconnectionPopup;
